import type { ShopifyProduct } from './shopifyDataTransformers';
import type { GalleryMedia } from '../components/CameraGalleryData';

/**
 * Creates fallback gallery media for when a product is not found
 */
function createFallbackGalleryMedia(id: string): GalleryMedia {
  return {
    productId: id,
    productName: 'Product Not Found',
    photos: [
      'https://placehold.co/300x400/E74C3C/white?text=NotFound1',
      'https://placehold.co/300x400/E74C3C/white?text=NotFound2',
      'https://placehold.co/300x400/E74C3C/white?text=NotFound3'
    ],
    videos: [
      'https://placehold.co/300x400/87483A/white?text=NotFoundV1',
      'https://placehold.co/300x400/87483A/white?text=NotFoundV2',
      'https://placehold.co/300x400/87483A/white?text=NotFoundV3'
    ]
  };
}

/**
 * Safely gets a URL from product images or returns a fallback
 */
function getFirstProductImage(product: ShopifyProduct): string {
  if (product.images?.edges && product.images.edges.length > 0) {
    const edge = product.images.edges[0];
    if (edge && edge.node && edge.node.url) {
      return edge.node.url;
    }
  }
  return 'https://placehold.co/300x400/E74C3C/white?text=NoPhoto';
}

/**
 * Extracts image URLs from product images
 */
function getProductImageUrls(product: ShopifyProduct, count: number, startIndex: number): string[] {
  const urls: string[] = [];
  
  if (product.images?.edges) {
    const edges = product.images.edges;
    const endIndex = Math.min(startIndex + count, edges.length);
    
    for (let i = startIndex; i < endIndex; i++) {
      const edge = edges[i];
      if (edge && edge.node && edge.node.url) {
        urls.push(edge.node.url);
      }
    }
  }
  
  return urls;
}

/**
 * AIDEV-NOTE: MediaImage Metafield Handling
 * Shopify can store media in metafields using MediaImage references (GIDs)
 * These references appear as values like "gid://shopify/MediaImage/45814570811702"
 * To properly resolve these to actual image URLs, we need to:
 * 1. Request the reference field in GraphQL queries (see queries.ts)
 * 2. Check for both reference.image.url (from MediaImage) and direct URL values
 * 3. Fallback to filename matching when neither is available
 * 
 * The GraphQL query needs to include:
 * reference {
 *   ... on MediaImage {
 *     image {
 *       url
 *     }
 *   }
 * }
 */
function getMetafieldImageUrl(metafield: any, product: ShopifyProduct): string | null {
  if (!metafield) return null;
  
  // Case 1: Check for MediaImage reference (preferred)
  if (metafield.reference && metafield.reference.image && metafield.reference.image.url) {
    return metafield.reference.image.url;
  }
  
  // Case 2: Check for direct URL in value
  if (metafield.value && typeof metafield.value === 'string') {
    if (metafield.value.includes('http')) {
      return metafield.value;
    }
    
    // Case 3: Value might be a filename, try to match with product images
    return processFilenameValue(metafield.value, product);
  }
  
  return null;
}

/**
 * Processes a metafield value that might be a filename
 */
function processFilenameValue(value: string, product: ShopifyProduct): string {
  
  // Try to find a matching image in the product images
  if (product.images?.edges) {
    // First pass: try direct matching
    for (const edge of product.images.edges) {
      if (edge.node && edge.node.url) {
        // Try to match by filename or part of filename
        if (edge.node.url.includes(value) || 
            edge.node.url.includes(value.replace(/\.[^.]+$/, ''))) {
          return edge.node.url;
        }
      }
    }
    
    // Second pass: try more flexible matching
    for (const edge of product.images.edges) {
      if (edge.node && edge.node.url) {
        // Get the base filename without path or extension
        const valueBase = value.replace(/\.[^.]+$/, '').toLowerCase();
        const urlParts = edge.node.url.split('/');
        const filename: string = (urlParts.length > 0) ? (urlParts[urlParts.length - 1] || '') : '';
        const filenameBase = filename.replace(/\.[^.]+$/, '').toLowerCase();
        
        // Check if any parts match
        const valueWords = valueBase.split(/[_\-\s.]+/);
        const filenameWords = filenameBase.split(/[_\-\s.]+/);
        
        // Count matching words
        let matchCount = 0;
        for (const word of valueWords) {
          if (word.length > 2 && filenameWords.includes(word)) {
            matchCount++;
          }
        }
        
        // If we have at least one significant word match, use this image
        if (matchCount > 0) {
          return edge.node.url;
        }
      }
    }
  }
  
  // Fallback to the first product image
  return getFirstProductImage(product);
}

/**
 * Legacy function for backward compatibility, uses the new getMetafieldImageUrl internally
 * @deprecated Use getMetafieldImageUrl instead
 */
// Keeping this function commented out since we're now using getMetafieldImageUrl directly
// function processMetafieldValue(value: string, product: ShopifyProduct): string {
//   // Create a simple metafield-like object with just the value
//   const simplifiedField = { value };
//   
//   // Use the new function
//   return getMetafieldImageUrl(simplifiedField, product) || getFirstProductImage(product);
// }

/**
 * Generates camera gallery data from Shopify products
 * 
 * @param shopifyProducts Array of Shopify products
 * @param selectedProductIds Array of selected product handles
 * @returns Gallery media data for the camera viewer
 */
export function generateCameraGalleryData(
  shopifyProducts: ShopifyProduct[],
  selectedProductIds: string[]
): GalleryMedia[] {
  // Filter out invalid products
  const productsArray = shopifyProducts.filter(product => product && product.handle);
  
  return selectedProductIds.map(handle => {
    if (!handle) {
      console.warn('Invalid handle in selectedProductIds');
      return createFallbackGalleryMedia('invalid-handle');
    }
    
    // Find the product by handle
    const product = productsArray.find(product => product && product.handle === handle);
    
    if (!product) {
      // Fallback data if product not found
      return createFallbackGalleryMedia(handle);
    }
    
    // Default photo and video URLs arrays
    let photoUrls: string[] = [];
    let videoUrls: string[] = [];
    
    // Default photo labels
    const photoLabels = ["Landscape", "Close Up", "Low Light"];
    
    // Try to get camera metafields
    if (product.metafields && Array.isArray(product.metafields)) {
      // Find camera metafields
      const landscapeField = product.metafields.find(
        field => field && field.key && field.namespace &&
          typeof field.key === 'string' &&
          field.key.toLowerCase() === 'landscape' &&
          field.namespace === 'custom'
      );
      
      const closeUpField = product.metafields.find(
        field => field && field.key && field.namespace &&
          typeof field.key === 'string' &&
          field.key.toLowerCase() === 'close_up' &&
          field.namespace === 'custom'
      );
      
      const lowLightField = product.metafields.find(
        field => field && field.key && field.namespace &&
          typeof field.key === 'string' &&
          field.key.toLowerCase() === 'low_light' &&
          field.namespace === 'custom'
      );
      
      // Log basic metafield information with enhanced detection for references
      
      // Check if we have any metafields with either value or reference
      const hasLandscapeMetafield = (landscapeField?.value && typeof landscapeField.value === 'string') || 
                                   (landscapeField?.reference?.image?.url !== undefined);
      const hasCloseUpMetafield = (closeUpField?.value && typeof closeUpField.value === 'string') || 
                                 (closeUpField?.reference?.image?.url !== undefined);
      const hasLowLightMetafield = (lowLightField?.value && typeof lowLightField.value === 'string') || 
                                  (lowLightField?.reference?.image?.url !== undefined);
      
      // Process metafields if any exist
      if (hasLandscapeMetafield || hasCloseUpMetafield || hasLowLightMetafield) {
        
        // Initialize photoUrls with placeholders
        photoUrls = [
          getFirstProductImage(product),
          getFirstProductImage(product),
          getFirstProductImage(product)
        ];
        
        // Replace with metafield values where available - using our new utility function
        if (landscapeField) {
          const landscapeUrl = getMetafieldImageUrl(landscapeField, product);
          if (landscapeUrl) {
            photoUrls[0] = landscapeUrl;
          }
        }
        
        if (closeUpField) {
          const closeUpUrl = getMetafieldImageUrl(closeUpField, product);
          if (closeUpUrl) {
            photoUrls[1] = closeUpUrl;
          }
        }
        
        if (lowLightField) {
          const lowLightUrl = getMetafieldImageUrl(lowLightField, product);
          if (lowLightUrl) {
            photoUrls[2] = lowLightUrl;
          }
        }
      } else {
      }
    }
    
    // If we still don't have photos, use product images
    if (photoUrls.length === 0) {
      photoUrls = getProductImageUrls(product, 3, 0);
      
      // If still no photos, use placeholder
      if (photoUrls.length === 0) {
        photoUrls = ['https://placehold.co/300x400/E74C3C/white?text=NoPhoto'];
      }
    }
    
    // Get video URLs
    videoUrls = getProductImageUrls(product, 2, 3);
    
    // If no videos, reuse photos
    if (videoUrls.length === 0) {
      videoUrls = [...photoUrls].slice(0, 2);
    }
    
    // Ensure we have at least 2 videos
    if (videoUrls.length === 1 && videoUrls[0]) {
      videoUrls.push(videoUrls[0]);
    }
    
    // Define video labels
    const videoLabels = ["Video 1", "Video 2"];
    
    // Get model name from metafield if available, otherwise use product title
    let modelName = product.title;
    
    if (product.metafields && Array.isArray(product.metafields)) {
      const modelField = product.metafields.find(
        field => field && field.key && field.namespace &&
          typeof field.key === 'string' &&
          field.key.toLowerCase() === 'model' &&
          (field.namespace === 'specifications' || field.namespace === 'custom')
      );

      if (modelField && modelField.value) {
        // Handle case where value is a JSON array: ["iPhone 15 Plus"]
        try {
          const parsedValue = JSON.parse(modelField.value);
          if (Array.isArray(parsedValue) && parsedValue.length > 0) {
            modelName = parsedValue[0];
          } else {
            modelName = modelField.value;
          }
        } catch (e) {
          // If not valid JSON, use the value directly
          modelName = modelField.value;
        }
      }
    }
    
    return {
      productId: product.handle,
      productName: modelName,
      photos: photoUrls,
      videos: videoUrls,
      photoLabels: photoLabels,
      videoLabels: videoLabels
    };
  });
}